/**
 * SubtaskDrawer.jsx
 *
 * Full-page modal for viewing and editing a subtask.
 * Replaces the old side-panel drawer with a centered, full-screen overlay
 * that mirrors the layout and interactivity of the main TaskDetails page.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { parentTaskVisibility, isTaskClosed, canEditSubtask, canFullyEditSubtask } from '../utils/taskRules';
import CommentsPanel from './CommentsPanel';
import AttachmentSection from './AttachmentSection';
import { toast } from 'react-hot-toast';

const PRIORITIES = ['Critical', 'Major', 'Minor'];
const STATUSES   = ['Open', 'Ongoing', 'Blocked', 'On Hold', 'Closed'];

const PRIORITY_BADGE = {
  Critical: 'bg-red-50 text-red-700 border border-red-200',
  Major:    'bg-yellow-50 text-yellow-700 border border-yellow-200',
  Minor:    'bg-green-50 text-green-700 border border-green-200',
};
const STATUS_BADGE = {
  Open:      'bg-blue-50 text-blue-700 border border-blue-200',
  Ongoing:   'bg-yellow-50 text-yellow-700 border border-yellow-200',
  Blocked:   'bg-red-50 text-red-700 border border-red-200',
  'On Hold': 'bg-orange-50 text-orange-700 border border-orange-200',
  Closed:    'bg-gray-100 text-gray-500 border border-gray-200',
};
const PRIORITY_ACTIVE = {
  Critical: 'border-red-400 bg-red-50 text-red-700',
  Major:    'border-yellow-400 bg-yellow-50 text-yellow-700',
  Minor:    'border-[#7AA33E] bg-[#f4f9ec] text-[#5a8a2a]',
};
const TASK_STATUS_BADGE = {
  Open:      'bg-blue-50 text-blue-700',
  Ongoing:   'bg-yellow-50 text-yellow-700',
  Blocked:   'bg-red-50 text-red-700',
  'On Hold': 'bg-orange-50 text-orange-700',
  Closed:    'bg-gray-100 text-gray-500',
};

export default function SubtaskDrawer({ subtask, onClose, onUpdated, canEdit, currentUser, onViewParentTask }) {
  const apiUrl = process.env.REACT_APP_API_URL;
  const token  = localStorage.getItem('jwt') || localStorage.getItem('token');

  const user = currentUser || (() => {
    try { return JSON.parse(atob(token.split('.')[1])); } catch { return {}; }
  })();

  const [parentTask, setParentTask]     = useState(null);
  const [saving, setSaving]             = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);

  // ── Edit state mirrors TaskDetails management panel ──────────────────────
  const [editPriority, setEditPriority] = useState('');
  const [editStatus, setEditStatus]     = useState('');
  const [editDueDate, setEditDueDate]   = useState('');
  const [prioritySaving, setPrioritySaving] = useState(false);

  useEffect(() => {
    if (!subtask) return;
    setParentTask(null);
    setCommentsOpen(false);
    setEditPriority('');
    setEditStatus('');
    setEditDueDate('');

    axios.get(`${apiUrl}/subtasks/${subtask.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      if (res.data?.parentTask) setParentTask(res.data.parentTask);
    }).catch(() => {});
  }, [subtask, apiUrl, token]);

  if (!subtask) return null;

  const parentClosed        = isTaskClosed(parentTask);
  const subtaskClosed       = isTaskClosed(subtask);
  const effectivelyReadOnly = subtaskClosed || parentClosed;

  // canEditSubtask  → true for manager, creator, assignee (used for status buttons)
  // canFullyEditSubtask → true for manager and creator only (used for Manage panel)
  const editAllowed      = canEdit && canEditSubtask(user, subtask, parentTask);
  const fullEditAllowed  = canEdit && canFullyEditSubtask(user, subtask, parentTask);
  const ptVisibility     = parentTaskVisibility(user, parentTask);

  const userId       = user?.userId || user?.id;
  const isManager    = (user?.role || '').toLowerCase() === 'manager' ||
                       (user?.role || '') === 'Manager';
  const isCreator    = Number(subtask.created_by) === Number(userId);
  // Manage panel: manager or creator only (already enforced by canFullyEditSubtask,
  // but keep the explicit isManager||isCreator check for clarity)
  const manageAllowed = fullEditAllowed && !effectivelyReadOnly && (isManager || isCreator);

  // Status buttons: available to assignee + manager + creator
  const statusAllowed = editAllowed && !effectivelyReadOnly;

  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const fmtFull = (d) => d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';
  const isOverdue = (d, s) => d && s !== 'Closed' && new Date(d) < new Date();

  const initials = subtask.assignee_name
    ? subtask.assignee_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : '?';

  // ── Save priority / status / due date (mirrors TaskDetails adminUpdate) ───
  const handleSaveEdit = async () => {
    setPrioritySaving(true);
    try {
      const payload = {};
      if (editPriority) payload.priority = editPriority;
      if (editStatus)   payload.status   = editStatus;
      if (editDueDate !== '') payload.due_date = editDueDate || null;

      await axios.put(`${apiUrl}/subtasks/${subtask.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const fresh = await axios.get(`${apiUrl}/subtasks/${subtask.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onUpdated(fresh.data?.subtask || fresh.data);
      setEditPriority('');
      setEditStatus('');
      setEditDueDate('');
      toast.success('Subtask updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error saving changes.');
    } finally {
      setPrioritySaving(false);
    }
  };

  // ── Quick status update (mirrors TaskDetails status buttons) ──────────────
  const handleStatusUpdate = async (newStatus) => {
    setSaving(true);
    try {
      await axios.patch(`${apiUrl}/subtasks/${subtask.id}/status`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const fresh = await axios.get(`${apiUrl}/subtasks/${subtask.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onUpdated(fresh.data?.subtask || fresh.data);
      toast.success('Status updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error updating status');
    } finally {
      setSaving(false);
    }
  };

  const activePriority = editPriority || subtask.priority;
  const activeDueDate  = editDueDate !== '' ? editDueDate : (subtask.due_date ? subtask.due_date.split('T')[0] : '');

  return (
    <>
    {/* ── Full-page overlay ── */}
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-full flex items-start justify-center py-8 px-4">
        <div className="w-full max-w-4xl">

          {/* ── Top bar ── */}
          <div className="flex items-center justify-between mb-5">
            <button
              onClick={onClose}
              className="inline-flex items-center gap-2 text-sm text-gray-200 hover:text-white transition group"
            >
              <svg className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              Back to Task
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 text-white hover:bg-white/20 transition"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* ── Read-only banner ── */}
          {effectivelyReadOnly && (
            <div className="mb-4 px-4 py-3 rounded-xl bg-gray-100 border border-gray-200 flex items-center gap-2 text-sm text-gray-500 font-medium">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              {parentClosed && !subtaskClosed
                ? 'The parent task is closed — this subtask is read only.'
                : 'This subtask is closed and is read only.'}
            </div>
          )}

          {/* ── Subtask header card ── */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-3">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Subtask #{subtask.id}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_BADGE[subtask.status] || 'bg-gray-100 text-gray-500'}`}>
                    {subtask.status}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${PRIORITY_BADGE[subtask.priority] || 'bg-gray-100 text-gray-500'}`}>
                    {subtask.priority}
                  </span>
                  {isOverdue(subtask.due_date, subtask.status) && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-600 border border-red-200">
                      ⚠ Overdue
                    </span>
                  )}
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">{subtask.title}</h1>
                {subtask.description && (
                  <p className="text-gray-500 text-sm leading-relaxed">{subtask.description}</p>
                )}
              </div>
            </div>

            {/* Meta grid */}
            <div className="mt-5 pt-5 border-t border-gray-100 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Assigned To</p>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-[#e5f3d4] flex items-center justify-center text-[#7AA33E] font-bold text-xs flex-shrink-0">
                    {initials}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800 leading-tight">{subtask.assignee_name || '—'}</p>
                    {subtask.assignee_employee_id && (
                      <p className="text-xs text-gray-400">{subtask.assignee_employee_id}</p>
                    )}
                  </div>
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Created By</p>
                <p className="text-sm font-medium text-gray-800">{subtask.created_by_name || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Due Date</p>
                <p className={`text-sm font-medium ${isOverdue(subtask.due_date, subtask.status) ? 'text-red-600' : 'text-gray-800'}`}>
                  {subtask.due_date ? fmt(subtask.due_date) : '—'}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Created</p>
                <p className="text-sm font-medium text-gray-800">{fmtFull(subtask.created_at)}</p>
              </div>
            </div>
          </div>

          {/* ── Update Status card (mirrors TaskDetails) ── */}
          {statusAllowed && (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-3">
              <div className="flex items-center gap-2 mb-1">
                <span className="w-1 h-5 rounded-full bg-[#7AA33E] inline-block" />
                <h2 className="text-sm font-bold text-gray-800">Update Status</h2>
              </div>
              <p className="text-xs text-gray-400 mb-5">Change the current status of this subtask</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'Open',     label: 'Open',        color: 'bg-blue-50 border-blue-200 text-blue-700',      active: 'bg-blue-500 border-blue-500 text-white' },
                  { value: 'Ongoing',  label: 'In Progress', color: 'bg-yellow-50 border-yellow-200 text-yellow-700', active: 'bg-yellow-400 border-yellow-400 text-white' },
                  { value: 'Blocked',  label: 'Blocked',     color: 'bg-red-50 border-red-200 text-red-700',          active: 'bg-red-500 border-red-500 text-white' },
                  { value: 'On Hold',  label: 'On Hold',     color: 'bg-orange-50 border-orange-200 text-orange-700', active: 'bg-orange-400 border-orange-400 text-white' },
                  { value: 'Closed',   label: 'Closed',      color: 'bg-gray-50 border-gray-200 text-gray-500',       active: 'bg-gray-600 border-gray-600 text-white' },
                ].map(({ value, label, color, active }) => (
                  <button
                    key={value}
                    type="button"
                    disabled={saving}
                    onClick={() => handleStatusUpdate(value)}
                    className={`px-4 py-2 rounded-xl border-2 text-xs font-bold transition ${
                      subtask.status === value ? active : `${color} hover:opacity-80`
                    } disabled:opacity-50`}
                  >
                    {subtask.status === value && <span className="mr-1">✓</span>}{label}
                  </button>
                ))}
              </div>
              {saving && (
                <div className="flex items-center gap-2 mt-4 text-xs text-gray-400">
                  <div className="w-3.5 h-3.5 border-2 border-[#7AA33E] border-t-transparent rounded-full animate-spin" />
                  Saving...
                </div>
              )}
            </div>
          )}

          {/* ── Comments ── */}
          {parentTask && (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-3">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Comments</h2>
                  <p className="text-xs text-gray-400 mt-0.5">Comments on this subtask and its parent task</p>
                </div>
                <button
                  onClick={() => setCommentsOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#23283a] text-white text-sm font-semibold hover:bg-gray-800 transition"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  Open Comments
                </button>
              </div>
            </div>
          )}

          {/* ── Attachments ── */}
          <AttachmentSection
            taskId={subtask.task_id}
            subtaskId={subtask.id}
            readOnly={effectivelyReadOnly}
            currentUserId={user?.userId || user?.id}
            title="Subtask Attachments"
          />

          {/* ── Manage Subtask panel (mirrors TaskDetails Manage Task) ── */}
          {manageAllowed && (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-3 mt-3">
              <div className="flex items-center gap-2 mb-5">
                <span className="w-1 h-5 rounded-full bg-[#7AA33E] inline-block" />
                <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide">Manage Subtask</h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* Priority */}
                <div className="flex flex-col gap-3">
                  <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide">Priority</h3>
                  <div className="flex gap-2 flex-wrap">
                    {PRIORITIES.map(p => (
                      <button key={p}
                        onClick={() => setEditPriority(p)}
                        className={`px-3 py-1.5 rounded-xl border-2 text-xs font-bold transition ${
                          activePriority === p
                            ? PRIORITY_ACTIVE[p]
                            : 'border-gray-200 bg-white text-gray-500 hover:border-gray-400'
                        }`}
                      >{p}</button>
                    ))}
                  </div>
                </div>

                {/* Status select */}
                <div className="flex flex-col gap-3">
                  <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide">Status</h3>
                  <select
                    value={editStatus || subtask.status}
                    onChange={e => setEditStatus(e.target.value)}
                    className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white transition appearance-none cursor-pointer"
                  >
                    {STATUSES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>

                {/* Due Date */}
                <div className="flex flex-col gap-3">
                  <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide">Due Date</h3>
                  <input
                    type="date"
                    value={activeDueDate}
                    onChange={e => setEditDueDate(e.target.value)}
                    className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white transition"
                  />
                </div>

                {/* Save button */}
                <div className="flex flex-col justify-end">
                  <button
                    disabled={prioritySaving}
                    onClick={handleSaveEdit}
                    className="px-4 py-2.5 rounded-xl bg-[#7AA33E] text-white text-xs font-semibold hover:bg-[#6a9335] disabled:opacity-50 transition shadow-sm shadow-[#7AA33E]/20 flex items-center gap-2"
                  >
                    {prioritySaving ? (
                      <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving…</>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Parent Task context card ── */}
          {parentTask && ptVisibility !== 'none' && (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-3">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="w-1 h-5 rounded-full bg-gray-300 inline-block" />
                  <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wide">Parent Task</h2>
                </div>
                {ptVisibility === 'button' && onViewParentTask && (
                  <button
                    onClick={() => { onViewParentTask(parentTask); onClose(); }}
                    className="flex items-center gap-1.5 text-xs text-[#7AA33E] font-semibold hover:underline"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    Open Parent Task
                  </button>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-base font-semibold text-gray-800">{parentTask.title}</p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${TASK_STATUS_BADGE[parentTask.status] || 'bg-gray-100 text-gray-500'}`}>
                    {(parentTask.status || '').replace('_', ' ')}
                  </span>
                  {parentTask.description && (
                    <p className="text-xs text-gray-400 mt-1 leading-relaxed">{parentTask.description}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Bottom spacing */}
          <div className="h-8" />
        </div>
      </div>
    </div>

    {/* Comments panel */}
    {parentTask && (
      <CommentsPanel
        open={commentsOpen}
        onClose={() => setCommentsOpen(false)}
        taskId={parentTask.id}
        task={parentTask}
        currentUserId={user?.userId || user?.id}
        token={token}
        lockedSubtask={{ id: subtask.id, title: subtask.title }}
      />
    )}
    </>
  );
}
